import { useState } from 'react';

import { dashboardT } from '../../../../../lib/messages';
import { usePlacementStatusCountsQuery, usePlacementsQuery } from '../../hooks';
import {
  defaultPlacementsFilter,
  type PlacementSortDirection,
  type PlacementSortProperty,
  type PlacementStatus,
  type PlacementsFilter,
  type PlacementType,
} from '../../model';
import { usePlacementsRouteFilters } from '../../usePlacementsRouteFilters';
import { PlacementsDataTable } from './PlacementsDataTable';
import type { PlacementsTableProps } from './PlacementsTable';

interface PlacementsFilterController {
  setSearch: (searchTerm: string) => void;
  setStatuses: (statuses: PlacementStatus[]) => void;
  setTypes: (types: PlacementType[]) => void;
  setPagination: (page: number, size: number) => void;
  setSort: (
    sortProperty: PlacementSortProperty | null,
    sortDirection: PlacementSortDirection,
  ) => void;
}

export function RoutePlacementsTable(props: PlacementsTableProps) {
  const routeFilters = usePlacementsRouteFilters();
  const { filter } = routeFilters;
  const query = usePlacementsQuery(filter);
  const countsQuery = usePlacementStatusCountsQuery(filter);

  return (
    <PlacementsDataTable
      title={props.title ?? dashboardT('placements.title')}
      tableLabel={props.tableLabel}
      items={query.data?.content ?? []}
      totalItems={query.data?.totalElements ?? 0}
      filter={filter}
      loading={query.isLoading}
      fetching={query.isFetching}
      error={query.isError}
      statusCounts={countsQuery.data}
      showCreateAction={props.showCreateAction}
      onSearch={routeFilters.setSearch}
      onStatusesChange={routeFilters.setStatuses}
      onTypesChange={routeFilters.setTypes}
      onPaginationChange={routeFilters.setPagination}
      onSortChange={routeFilters.setSort}
      onRefresh={() => void query.refetch()}
    />
  );
}

export function ScopedPlacementsTable(
  props: PlacementsTableProps & {
    demandIntegrationId?: string | undefined;
    publisherId?: string | undefined;
  },
) {
  const [localFilter, setLocalFilter] = useState<PlacementsFilter>(() => ({
    ...defaultPlacementsFilter,
    statuses: [...defaultPlacementsFilter.statuses],
    types: [...defaultPlacementsFilter.types],
  }));
  const filter = {
    ...localFilter,
    ...(props.demandIntegrationId ? { demandIntegrationId: props.demandIntegrationId } : {}),
    ...(props.publisherId ? { publishersIds: [props.publisherId] } : {}),
  };
  const query = usePlacementsQuery(filter);
  const countsQuery = usePlacementStatusCountsQuery(filter);

  const controller: PlacementsFilterController = {
    setSearch: (searchTerm) => {
      setLocalFilter((current) => ({ ...current, searchTerm, page: 0 }));
    },
    setStatuses: (statuses) => {
      setLocalFilter((current) => ({ ...current, statuses, page: 0 }));
    },
    setTypes: (types) => {
      setLocalFilter((current) => ({ ...current, types, page: 0 }));
    },
    setPagination: (page, size) => {
      setLocalFilter((current) => ({
        ...current,
        page: size === current.size ? page : 0,
        size,
      }));
    },
    setSort: (sortProperty, sortDirection) => {
      setLocalFilter((current) => ({ ...current, sortProperty, sortDirection, page: 0 }));
    },
  };

  return (
    <PlacementsDataTable
      title={props.title ?? dashboardT('placements.title')}
      tableLabel={props.tableLabel}
      items={query.data?.content ?? []}
      totalItems={query.data?.totalElements ?? 0}
      filter={filter}
      loading={query.isLoading}
      fetching={query.isFetching}
      error={query.isError}
      statusCounts={countsQuery.data}
      showCreateAction={props.showCreateAction}
      publisherScoped={Boolean(props.publisherId)}
      onSearch={controller.setSearch}
      onStatusesChange={controller.setStatuses}
      onTypesChange={controller.setTypes}
      onPaginationChange={controller.setPagination}
      onSortChange={controller.setSort}
      onRefresh={() => void query.refetch()}
    />
  );
}
